import SettlementConfirm from '@views/settlement/confirm';
import MemberRegister from '@views/member/register';
import MemberConfirm from '@views/member/confirm';
import Intro from '@views/intro';
import Loading from '@views/loading';
import { LoadingContext } from '@views/loading/context';
import { useLoading as useInitLoading } from '@views/loading/initLoading';
import { useLoading as useSettlementLoading } from '@views/loading/settlementLoading';
import { useLoading as useReauthorizeLoading } from '@views/loading/reauthorizeLoading';
import AuthSms from '@views/auth/sms';
import LoginNp from '@views/login/np';
import LoginAtone from '@views/login/atone';
import AuthNg from '@views/fail/auth-ng';
import OfficialIdentification from '@views/fail/official-identification';
import SpappLogin from '@views/fail/spapp-login';
import AuthCall from '@views/auth/call';
import { AuthNgFailContext } from '@views/fail/auth-ng/composable';
import atoneNgFailContext from '@views/fail/auth-ng/AtoneAuthNgContext';
import { SpappLoginFailContext } from '@views/fail/spapp-login/composable';
import { FailCartContext } from '@views/fail/cart/composable';
import FailCartConfirm from '@views/fail/cart';
import { OfficialIdentificationFailContext } from '@views/fail/official-identification/composable';
import AppEmptyRoute from '@berlin-front/common/components/elements/AppEmptyRoute';
import atoneMemberRegisterFactoryParam from '@views/member/register/atoneMemberRegisterFactoryParam';
import { HEADER_TYPE } from '@berlin-front/common/components/clusters/ModalHeader/composable';
import { AtoneNPMemberRegisterGroupContext } from '@views/member/AtoneNPMemberRegisterGroupContext';
import { AtoneMemberRegisterGroupContext } from '@views/member/AtoneMemberRegisterGroupContext';
import { CallAuthContext } from '@views/auth/call/composable';
import { IntroContext } from '@views/intro/composable';
import { ConfirmSettlementContext } from '@views/settlement/confirm/composable';
import { MemberRequestContext } from '@views/member/MemberRequestContext';
import { MemberRegisterContext, useMemberRegisterFactory } from '@views/member/register/composable';
import { ConfirmMemberContext, useMemberConfirmFactory } from '@views/member/confirm/composable';
import { useSmsAuth as useSettlementSmsAuth } from '@views/auth/sms/settlementAuth';
import { useRegisterSmsAuthWith } from '@views/auth/sms/memberRegisterAuth';
import { AtoneLoginContext } from '@views/login/atone/composable';
import { NpLoginContext } from '@views/login/np/composable';
import { SmsAuthContext } from '@views/auth/sms/context';

import { includes } from '@berlin-front/common/utils';
import { RouteConfig } from 'vue-router';
import { ROUTE_NAME } from './route-name';

const atoneRoute: RouteConfig = {
  path: '/atone',
  // データのライフサイクルが複数の画面に跨る場合は、AppEmptyRouteを使用する。
  // ここで定義したコンテキストでデータの持ち回しを行う。
  components: {
    default: AppEmptyRoute,
    contextProvider: MemberRequestContext.provider,
  },
  children: [
    {
      // atone決済審査中ローディング中画面
      path: 'loading/settlement/:point',
      name: ROUTE_NAME.ATONE_LOADING_SETTLEMENT,
      meta: {
        cancelModalType: 'reject',
        headerType: HEADER_TYPE.TITLE_ONLY,
        headerTitle: '注文確定',
      },
      components: {
        default: Loading,
        contextProvider: LoadingContext.provider,
      },
      props: {
        // ローディングコンテキストは差し替えで表示する。
        contextProvider: useSettlementLoading,
      },
    },
    {
      // atone決済再審査中画面
      path: 'loading/reauthorize',
      name: ROUTE_NAME.ATONE_LOADING_REAUTHORIZE,
      meta: {
        cancelModalType: 'reject',
        headerType: HEADER_TYPE.TITLE_ONLY,
        headerTitle: '注文確定',
      },
      components: {
        default: Loading,
        contextProvider: LoadingContext.provider,
      },
      props: {
        // ローディングコンテキストは差し替えで表示する。
        contextProvider: useReauthorizeLoading,
      },
    },
    {
      // イントロ画面。
      path: 'intro',
      name: ROUTE_NAME.ATONE_INTRO,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: Intro,
        contextProvider: IntroContext.provider,
      },
    },
    {
      // ログイン画面
      path: 'login/atone',
      name: ROUTE_NAME.ATONE_LOGIN_ATONE,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: LoginAtone,
        contextProvider: AtoneLoginContext.provider,
      },
    },
    {
      // 決済時sms認証画面
      path: 'auth/sms/settlement',
      name: ROUTE_NAME.ATONE_AUTH_SMS_SETTLEMENT,
      meta: {
        cancelModalType: 'shopping',
        headerTitle: 'SMS認証',
        headerType: HEADER_TYPE.TITLE_ONLY,
      },
      components: {
        default: AuthSms,
        contextProvider: SmsAuthContext.provider,
      },
      props: {
        contextProvider: useSettlementSmsAuth,
        default: (route) => {
          const { previous } = route.query;
          return typeof previous === 'string' && includes(...Object.values(ROUTE_NAME))(previous) ? { previous } : {};
        },
      },
    },
    {
      // 決済画面。
      path: 'settlement/confirm',
      name: ROUTE_NAME.ATONE_SETTLEMENT_CONFIRM,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.SHOP_TITLE,
        headerTitle: '注文確定',
      },
      components: {
        default: SettlementConfirm,
        // ここに書いたプロバイダは、ContextProviderRouterViewによって、Settlementの親として差し込まれる。
        contextProvider: ConfirmSettlementContext.provider,
      },
    },
    {
      path: 'np-member',
      components: {
        default: AppEmptyRoute,
        contextProvider: AtoneNPMemberRegisterGroupContext.provider,
      },
      children: [
        {
          // NPアカウントでログイン画面
          path: 'login',
          name: ROUTE_NAME.ATONE_MEMBER_NP_LOGIN,
          meta: {
            cancelModalType: 'shopping',
            headerType: HEADER_TYPE.SHOP_PRICE,
          },
          components: {
            default: LoginNp,
            contextProvider: NpLoginContext.provider,
          },
        },
        {
          // 会員登録画面
          path: 'register',
          name: ROUTE_NAME.ATONE_NP_MEMBER_REGISTER,
          meta: {
            cancelModalType: 'shopping',
            headerTitle: '会員登録',
            headerType: HEADER_TYPE.TITLE_ONLY,
          },
          components: {
            default: MemberRegister,
            contextProvider: MemberRegisterContext.provider,
          },
          // contextProvider↑
          props: {
            contextProvider: {
              useMemberRegister: useMemberRegisterFactory(
                atoneMemberRegisterFactoryParam({
                  nextPage: ROUTE_NAME.ATONE_NP_MEMBER_CONFIRM,
                  prevPage: ROUTE_NAME.ATONE_INTRO,
                }),
              ),
            },
          },
        },
        {
          // 会員登録確認画面
          path: 'confirm',
          name: ROUTE_NAME.ATONE_NP_MEMBER_CONFIRM,
          meta: {
            cancelModalType: 'shopping',
            headerTitle: '会員情報の確認',
            headerType: HEADER_TYPE.TITLE_ONLY,
          },
          components: {
            default: MemberConfirm,
            contextProvider: ConfirmMemberContext.provider,
          },
          props: {
            contextProvider: {
              useMemberConfirm: useMemberConfirmFactory({
                nextPage: ROUTE_NAME.ATONE_NP_MEMBER_AUTH,
                prevPage: ROUTE_NAME.ATONE_NP_MEMBER_REGISTER,
              }),
            },
          },
        },
        {
          // 会員登録sms認証画面
          path: 'auth',
          name: ROUTE_NAME.ATONE_NP_MEMBER_AUTH,
          meta: {
            cancelModalType: 'shopping',
            headerTitle: 'SMS認証',
            headerType: HEADER_TYPE.TITLE_ONLY,
          },
          components: {
            default: AuthSms,
            contextProvider: SmsAuthContext.provider,
          },
          props: {
            contextProvider: useRegisterSmsAuthWith({
              registerPage: ROUTE_NAME.ATONE_NP_MEMBER_REGISTER,
              nextPage: ROUTE_NAME.ATONE_SETTLEMENT_CONFIRM,
            }),
          },
        },
      ],
    },
    {
      path: 'member',
      components: {
        default: AppEmptyRoute,
        contextProvider: AtoneMemberRegisterGroupContext.provider,
      },
      children: [
        {
          // 会員登録画面
          path: 'register',
          name: ROUTE_NAME.ATONE_MEMBER_REGISTER,
          meta: {
            cancelModalType: 'shopping',
            headerTitle: '会員登録',
            headerType: HEADER_TYPE.TITLE_ONLY,
          },
          components: {
            default: MemberRegister,
            contextProvider: MemberRegisterContext.provider,
          },
          // contextProvider↑
          props: {
            contextProvider: {
              useMemberRegister: useMemberRegisterFactory(
                atoneMemberRegisterFactoryParam({
                  nextPage: ROUTE_NAME.ATONE_MEMBER_CONFIRM,
                  prevPage: ROUTE_NAME.ATONE_INTRO,
                }),
              ),
            },
          },
        },
        {
          // 会員登録確認画面
          path: 'confirm',
          name: ROUTE_NAME.ATONE_MEMBER_CONFIRM,
          meta: {
            cancelModalType: 'shopping',
            headerTitle: '会員情報の確認',
            headerType: HEADER_TYPE.TITLE_ONLY,
          },
          components: {
            default: MemberConfirm,
            contextProvider: ConfirmMemberContext.provider,
          },
          props: {
            contextProvider: {
              useMemberConfirm: useMemberConfirmFactory({
                nextPage: ROUTE_NAME.ATONE_MEMBER_AUTH,
                prevPage: ROUTE_NAME.ATONE_MEMBER_REGISTER,
              }),
            },
          },
        },
        {
          // 会員登録sms認証画面
          path: 'auth',
          name: ROUTE_NAME.ATONE_MEMBER_AUTH,
          meta: {
            cancelModalType: 'shopping',
            headerTitle: 'SMS認証',
            headerType: HEADER_TYPE.TITLE_ONLY,
          },
          components: {
            default: AuthSms,
            contextProvider: SmsAuthContext.provider,
          },
          props: {
            contextProvider: useRegisterSmsAuthWith({
              registerPage: ROUTE_NAME.ATONE_MEMBER_REGISTER,
              nextPage: ROUTE_NAME.ATONE_SETTLEMENT_CONFIRM,
            }),
          },
        },
      ],
    },
    {
      // 着信認証画面
      path: 'auth/call',
      name: ROUTE_NAME.ATONE_AUTH_CALL,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: AuthCall,
        contextProvider: CallAuthContext.provider,
      },
    },
    {
      // アプリ認証画面
      path: 'fail/spapp-login',
      name: ROUTE_NAME.ATONE_FAIL_SPAPP_LOGIN,
      meta: {
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: SpappLogin,
        contextProvider: SpappLoginFailContext.provider,
      },
    },
    {
      // カートエラー
      path: 'fail/cart',
      name: ROUTE_NAME.ATONE_FAIL_CART,
      meta: {
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: FailCartConfirm,
        contextProvider: FailCartContext.provider,
      },
    },
    {
      // 公的証明証
      path: 'fail/official-identification',
      name: ROUTE_NAME.ATONE_FAIL_OFFICIAL_IDENTIFICATION,
      meta: {
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: OfficialIdentification,
        contextProvider: OfficialIdentificationFailContext.provider,
      },
    },
    {
      // 決済失敗
      path: 'fail/auth-ng',
      name: ROUTE_NAME.ATONE_FAIL_AUTH_NG,
      meta: {
        headerType: HEADER_TYPE.SHOP_PRICE,
      },
      components: {
        default: AuthNg,
        contextProvider: AuthNgFailContext.provider,
      },
      props: {
        contextProvider: atoneNgFailContext,
      },
    },
    {
      // atone起動ローディング画面
      path: 'loading',
      name: ROUTE_NAME.ATONE_LOADING,
      meta: {
        // ヘッダは非表示。
        cancelModalType: 'reject',
        headerType: HEADER_TYPE.NONE,
      },
      components: {
        default: Loading,
        contextProvider: LoadingContext.provider,
      },
      props: {
        // ローディングコンテキストは差し替えで表示する。
        contextProvider: useInitLoading,
      },
    },
  ],
};

export default atoneRoute;
